import { useEffect, useRef, useState } from 'react'
import { valueOf } from '@asvise/facts'
import {
  HOUSEHOLD_FACT_REGISTRY,
  PERSON_FACT_REGISTRY,
  type Answers,
  type Assessment,
  type HouseholdFactField,
  type Person,
  type PersonFactField,
} from '@asvise/household-engine/vocabulary'
import { HouseholdQuestion, PersonQuestion, settles } from './inputs'

/**
 * The questions something is waiting on, asked over the screen that asked for
 * them.
 *
 * The business app's sheet, which is where answering happens there too, and the
 * reason the long form stopped being the way in: a household that opens a
 * twenty-question page learns nothing until it finishes, and a household that
 * opens a board sees six areas and what each would need.
 *
 * One question at a time, with Back and a Skip that costs nothing. The skip is
 * the part that makes this honest: the board behind is already useful, every
 * area says what it is missing, and nothing is held up by a question somebody
 * does not want to answer today.
 *
 * TWO CALLERS, AND THEY ASK DIFFERENT THINGS. The board asks what one exposure
 * is waiting on, read from the assessment. The record asks one group of the
 * framework's question set, which is a topic rather than an exposure and spans
 * the household and every member in it. So an ask names its subject: group A is
 * three questions about each person and no question about the household, and a
 * sheet that could only hold household fields could not have asked it.
 */

/** One question, and who it is about. */
export type Ask =
  | { kind: 'HOUSEHOLD'; field: HouseholdFactField }
  | { kind: 'PERSON'; person: Person; field: PersonFactField }

export interface Asking {
  /** What the sheet says it is about: a domain label, or a phase title. */
  title: string
  asks: Ask[]
  /**
   * The one it was opened on, where it was opened from a row rather than from a
   * heading.
   *
   * The business app's `reveal`, and for its reason: a record of rows where
   * every row opens the same sheet at the top leaves the reader to find their
   * row again in a list they were just looking at.
   */
  at?: number
}

/** Every unanswered ask in a list, which is what a heading offers. */
export function unanswered(asks: Ask[], answers: Answers): Ask[] {
  return asks.filter((a) =>
    a.kind === 'HOUSEHOLD'
      ? valueOf(answers[a.field] as never) === null
      : valueOf(a.person.facts[a.field] as never) === null,
  )
}

const titleOf = (ask: Ask): string =>
  ask.kind === 'HOUSEHOLD'
    ? HOUSEHOLD_FACT_REGISTRY[ask.field].label
    : PERSON_FACT_REGISTRY[ask.field].label

export function Answering({
  asking,
  answers,
  onChange,
  assessment,
  onDone,
}: {
  asking: Asking
  answers: Answers
  onChange: (next: Answers) => void
  assessment: Assessment | null
  onDone: () => void
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const [index, setIndex] = useState(asking.at ?? 0)

  useEffect(() => {
    ref.current?.showModal()
  }, [])

  const close = () => ref.current?.close()

  const asks = asking.asks
  const ask = asks[Math.min(index, asks.length - 1)]
  if (!ask) return null

  /**
   * The member as the answers now hold them, not as they were when the sheet
   * opened.
   *
   * An ask carries the person so the sheet knows who it is about, and a copy of
   * a member taken at open is stale the moment anything is typed into it: the
   * second question of group A would have been writing into the household as it
   * was before the first was answered, and the first answer would be gone.
   */
  const person =
    ask.kind === 'PERSON' ? (answers.people.find((p) => p.id === ask.person.id) ?? null) : null
  if (ask.kind === 'PERSON' && person === null) return null

  const answered =
    ask.kind === 'HOUSEHOLD'
      ? valueOf(answers[ask.field] as never) !== null
      : valueOf(person!.facts[ask.field] as never) !== null
  const last = index >= asks.length - 1

  /**
   * What this answer would unblock, for a household field. Nothing for a
   * member's, because the engine reports the fields a calculation is missing
   * and those are the household's: a year of birth is read through the derived
   * profile rather than named by any calculation.
   */
  const unlocks = ask.kind === 'HOUSEHOLD' ? settles(assessment, ask.field) : []

  return (
    <dialog
      ref={ref}
      className="sheet"
      onClose={onDone}
      onClick={(e) => {
        if (e.target === ref.current) close()
      }}
    >
      <div className="sheet-body">
        <header className="answering-head">
          <span className="answering-where">
            {asking.title}
            {asks.length > 1 && (
              <>
                <span className="sep">·</span>
                {index + 1} of {asks.length}
              </>
            )}
          </span>
          <button type="button" className="sheet-close" onClick={close} aria-label="Close">
            ×
          </button>
        </header>

        <div className="answering-body">
          <h2>
            {titleOf(ask)}
            {/* Whose answer this is, where it is somebody's. Four members and
                eight questions each is thirty-two answers, and a sheet that
                said only "Year of birth" left the reader to remember which. */}
            {person && <span className="answering-who">{person.label}</span>}
          </h2>

          {ask.kind === 'HOUSEHOLD' ? (
            <HouseholdQuestion field={ask.field} answers={answers} onChange={onChange} />
          ) : (
            <PersonQuestion
              person={person!}
              field={ask.field}
              answers={answers}
              onChange={onChange}
            />
          )}

          {unlocks.length > 0 ? (
            <p className="answering-settles">
              Settles{' '}
              {unlocks.map((name, i) => (
                <span key={name}>
                  {i > 0 && ', '}
                  <strong>{name.toLowerCase()}</strong>
                </span>
              ))}
            </p>
          ) : (
            /*
              Said rather than left blank, and only while the question is still
              open. A question with nothing waiting on it is the reason it was
              not asked earlier, and somebody who came here anyway should be
              told they are ahead rather than behind.
            */
            !answered &&
            ask.kind === 'HOUSEHOLD' && (
              <p className="answering-settles quiet">
                Nothing is waiting on this one. It is worth answering, and nothing on the board is
                held up until it is.
              </p>
            )
          )}
        </div>

        <footer className="answering-foot">
          {index > 0 && (
            <button type="button" onClick={() => setIndex(index - 1)}>
              Back
            </button>
          )}
          {last ? (
            <button type="button" className="primary" onClick={close}>
              Done
            </button>
          ) : (
            <button type="button" className="primary" onClick={() => setIndex(index + 1)}>
              {answered ? 'Next' : 'Skip for now'}
            </button>
          )}
        </footer>
      </div>
    </dialog>
  )
}
